import { useFormikContext } from 'formik'
import * as React from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import * as Api from 'src/api'
import Portfolio from 'src/assets/icons/customIcons/menu-icons/Portfolio'
import { formatFileSize } from 'src/helpers/fns'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { type FileState } from 'src/hooks/fileUpload'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import BackButton from 'src/views/components/BackButton'
import { ErrorBoundary } from 'src/views/components/Error'
import ErrorElement from 'src/views/components/ErrorElement'
import FileIcon from 'src/views/components/FileIcon'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import PopoverComponent from 'src/views/components/PopoverComponent'
import UserPhoto from 'src/views/components/UserPhoto'
import FormikTinyMce from 'src/views/components/forms/FormikTinyMce'
import FileInput from 'src/views/components/forms/formik/FileInput'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextareaInput from 'src/views/components/forms/formik/TextareaInput'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

interface CompetencyCriteria {
  criteriaId: number
  text: string
}

interface Competency {
  competencyId: number
  criterias: CompetencyCriteria[]
}

interface FormikValues {
  readonly isDraft: boolean
  readonly mentorId: string
  readonly text: string
  readonly id: string
  readonly files: readonly FileState[]
  readonly competencies: Competency[]
}

export default function StudentPortfolioWorkPage(): JSX.Element | null {
  const t = useTranslatable()

  return (
    <>
      <Helmet title={t('portfolio:assignment')} />
      <React.Suspense fallback={<Loader className="m-auto flex" />}>
        <ErrorBoundary errorElement={<ErrorElement />}>
          <PageContent />
        </ErrorBoundary>
      </React.Suspense>
    </>
  )
}

function PageContent(): JSX.Element | null {
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()
  const locale = useLocale()
  const params = useParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [isSubmitted, setIsSubmitted] = React.useState(false)

  const sectionId = params.sectionId ?? ''
  const workId = params.workId ?? ''
  const hasValidParams = sectionId.length > 0 && workId.length > 0

  // A work reached from the section history can belong to a past section the student can no longer
  // fetch, so a failing section request must not block the work itself from rendering.
  const section = useApi({
    endpoint: Api.getStudentPortfolioSection,
    params: React.useMemo(() => ({ headers, args: { id: sectionId } }), [headers, sectionId]),
    shouldFetch: hasValidParams,
    suspense: false,
  })

  const work = useApi({
    endpoint: Api.getStudentPortfolioSectionWork,
    params: React.useMemo(() => ({ headers, args: { sectionId, id: workId } }), [headers, sectionId, workId]),
    shouldFetch: hasValidParams,
    suspense: false,
  })

  const workData = work.data
  const sectionData = section.data

  const initialValues = React.useMemo<FormikValues>(
    () => ({
      isDraft: true,
      id: workData?.id ?? '',
      mentorId: workData?.mentor?.id ?? '',
      text: workData?.text ?? '',
      competencies:
        workData?.competencies?.map((v) => ({
          competencyId: Number(v.id),
          criterias:
            v.criterias?.map((criteria) => ({
              criteriaId: Number(criteria.id),
              text: criteria.text ?? '',
            })) ?? [],
        })) ?? [],
      files:
        workData?.mediaFiles?.map((file) => ({
          status: 'DONE' as const,
          remoteFile: file,
        })) ?? [],
    }),
    [workData]
  )

  const redirectPath = `/${locale}/student/portfolio/sections/${sectionId}/works`

  const onSubmit = React.useCallback(
    async (values: FormikValues): Promise<void> => {
      if (!hasValidParams) return

      const { mentorId, text, files, competencies, isDraft } = values
      const filteredCompetencies =
        sectionData?.workType === '2'
          ? competencies.map(({ competencyId, criterias }) => ({
              competencyId,
              criterias: criterias.map(({ criteriaId, text }) => ({ criteriaId, text })),
            }))
          : competencies.map(({ competencyId }) => ({ competencyId }))

      const fileIds = files.flatMap((e) => e.remoteFile?.id ?? [])
      const body = {
        ...(text && { text }),
        mentorId,
        competencies: filteredCompetencies,
        files: fileIds,
      }

      if (isDraft) {
        await Api.putStudentPortfolioWork({
          headers,
          body,
          args: { id: sectionId, workId },
        })
      } else if (workData?.status === 'STATUS_REJECTED') {
        await Api.patchStudentPortfolioWork({
          headers,
          args: { id: sectionId, workId },
          body,
        })
      } else {
        await Api.postStudentPortfolioWork({
          headers,
          body,
          args: { id: sectionId, workId },
        })
      }

      toast.success(t('portfolio:assignment_submitted'))
      setIsSubmitted(true)
    },
    [sectionData?.workType, workData?.status, t, headers, hasValidParams, sectionId, workId]
  )

  React.useEffect(() => {
    if (isSubmitted) {
      navigate(redirectPath)
    }
  }, [isSubmitted, navigate, redirectPath])

  const breadcrumbsItems = React.useMemo(
    () => [
      { page: t('portfolio:dashboard'), path: `/${locale}/student/portfolio` },
      { page: sectionData?.name ?? '', path: redirectPath },
      { page: t('portfolio:assignment'), path: pathname },
    ],
    [t, locale, sectionData?.name, redirectPath, pathname]
  )

  const workStatus = workData?.status
  const isDraftOrRejected = workStatus === 'STATUS_DRAFT' || workStatus === 'STATUS_REJECTED'

  // The work is what this page renders; a past section that no longer resolves only costs the form,
  // so waiting on the section is limited to the statuses whose form needs it.
  const isLoading = work.isLoading || (isDraftOrRejected && section.isLoading)
  const isEditableView = isDraftOrRejected && sectionData != null
  const isReadOnlyView = !isLoading && workData != null && !isEditableView

  return (
    <>
      <Section
        title={t('portfolio:portfolio_assignment')}
        icon={<Portfolio />}
        breadcrubms={breadcrumbsItems}
        rightElement={<BackButton link={redirectPath} />}
      />
      {isReadOnlyView ? (
        <div className="text-primaryTextColor">
          <div className="mb-6">
            <h2 className="mb-3 border-b border-borderGrey py-2">{t('portfolio:assignment_competencies')}: </h2>
            {workData?.competencies != null
              ? workData.competencies.map((competency) => (
                  <div key={competency.id} className="mb-2">
                    <div className="mt-2">
                      {competency.name}
                      <PopoverComponent>
                        <span className="mb-3 flex py-2" dangerouslySetInnerHTML={{ __html: competency.description }} />
                        <div className="mb-6">
                          <div className="mb-2 flex flex-nowrap items-center pl-0 font-bold text-primaryTextColor">
                            {t('portfolio:criterias')}:
                          </div>
                          {competency.criterias?.map((criteria) => (
                            <div key={`${competency.id}-${criteria.id}`} className="my-2">
                              <label className="mb-3 text-primaryTextColor">
                                <div className="my-">
                                  {criteria.name}
                                  <div>
                                    <span className="p-2" dangerouslySetInnerHTML={{ __html: criteria.description }} />
                                  </div>
                                </div>
                              </label>
                            </div>
                          ))}
                        </div>
                      </PopoverComponent>
                    </div>

                    {competency.criterias?.map((criteria) => (
                      <div key={`${competency.id}-${criteria.id}`} className="mb-5 mt-4 rounded-card bg-card px-4 py-1">
                        <div className="mb-2.5 mt-3 flex">
                          <div className="mr-2">
                            <label>{t('portfolio:criterion')}:</label>
                          </div>
                          <div>
                            <div>{criteria.name}</div>
                          </div>
                        </div>
                        {criteria.text && (
                          <div key={`${competency.id}-${criteria.id}`} className="my-2.5 flex">
                            <div className="mr-2">
                              <label>{t('portfolio:description')}:</label>
                            </div>
                            <div>
                              <div>{criteria.text}</div>
                            </div>
                          </div>
                        )}
                        {criteria.score?.score !== undefined && (
                          <div className="my-2.5 flex">
                            <div className="mr-2">
                              <label>{t('common:score')}:</label>
                            </div>
                            <div>
                              <div>{criteria.score?.score}</div>
                            </div>
                          </div>
                        )}
                        {workStatus === 'STATUS_EVALUATED' && (
                          <div key={`${competency.id}-${criteria.id}`} className="my-2.5 flex">
                            <div className="mr-2">
                              <label>{t('portfolio:assignment_feedback')}:</label>
                            </div>
                            <div>
                              <div> {criteria.score?.feedback}</div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))
              : null}
          </div>
          {workData?.text && (
            <div className="mb-6">
              <h2 className="mb-3 border-b border-borderGrey py-2">{t('portfolio:assignment_description')}: </h2>
              <span dangerouslySetInnerHTML={{ __html: workData.text }} />
            </div>
          )}

          <div className="mb-6">
            <h2 className="mb-3 border-b border-borderGrey py-2">{t('portfolio:mentor')}: </h2>
            {workData?.mentor != null ? (
              <div className="text-title">
                <UserPhoto className="scale-[0.7]" user={workData.mentor} />
                <Link
                  to={`/${locale}/users/${workData.mentor.uid}`}
                  className="ml-2 whitespace-nowrap align-middle !no-underline hover:underline"
                  data-tooltip-id={`${workData.id}-lecturer`}
                >
                  <span className="ml-2">{workData.mentor.fullName}</span>
                </Link>
              </div>
            ) : (
              <span> - </span>
            )}
          </div>
          {workData?.mediaFiles != null && workData.mediaFiles.length > 0 && (
            <div className="mb-8">
              <h2 className="works-section mb-3 border-b border-borderGrey py-2">{t('file:attached_files')}: </h2>
              <div className="syllabus-content text-title">
                <div className="row-auto my-3">
                  <div className="col-auto mb-2">
                    {workData.mediaFiles.map((file) => (
                      <div key={file.id} className="mr-4 flex">
                        <FileIcon type={file.extension} />{' '}
                        <a
                          href={`/${locale}/media-files/${file.id}/download`}
                          className="mx-2 dark:text-white"
                          rel="noreferrer"
                          target="_blank"
                        >
                          {file.originalName}
                        </a>
                        <span className="text-sm text-captionColor">({formatFileSize(file.size)})</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="mb-8">
            <h2 className="mb-3 border-b border-borderGrey py-2">{t('portfolio:assignment_status')}: </h2>
            <div>
              {workStatus === 'STATUS_EVALUATED' && (
                <span className="text-primaryTextColor">{t('portfolio:status_evaluated')}</span>
              )}
              {workStatus === 'STATUS_PENDING' && <span>{t('portfolio:status_pending')}</span>}
              {workStatus === 'STATUS_REJECTED' && (
                <span className="text-primaryTextColor">{t('portfolio:status_rejected')}</span>
              )}
              {workStatus === 'STATUS_DRAFT' && (
                <span className="text-primaryTextColor">{t('portfolio:status_draft')}</span>
              )}
            </div>
          </div>
          {workData?.comment != null && workData.comment.length > 0 && (
            <div className="mb-6">
              <h2 className="mb-3 border-b border-borderGrey py-2">{t('portfolio:lecturer_comment')}: </h2>
              <div className="text-title text-primaryTextColor">{workData.comment}</div>
            </div>
          )}
        </div>
      ) : isEditableView ? (
        <Form initialValues={initialValues} onSubmit={onSubmit} enableReinitialize isConfirmable={!isSubmitted}>
          <FormError />
          {workData != null && sectionData != null && (
            <SuspenseWrapper>
              <FormContent work={workData} section={sectionData} sectionId={sectionId} />
            </SuspenseWrapper>
          )}
        </Form>
      ) : isLoading ? (
        <Loader className="m-auto flex" />
      ) : (
        <NoContent header={t('error:records_not_found')} image={Warning} />
      )}
    </>
  )
}

interface FormContentProps {
  readonly work: Api.StudentPortfolioUserWork
  readonly section: Api.Section
  readonly sectionId: string
}

function FormContent({ work, section, sectionId }: FormContentProps): React.ReactElement | null {
  const formik = useFormikContext<FormikValues>()
  const t = useTranslatable()
  const headers = useAuthenticatedHeaders()

  const mentors = useApi({
    endpoint: Api.getStudentPortfolioMentros,
    params: React.useMemo(() => ({ headers, args: { id: sectionId } }), [headers, sectionId]),
  })

  const sectionCompetencies = useApi({
    endpoint: Api.getStudentPortfolioCompetencies,
    params: React.useMemo(() => ({ headers, args: { id: sectionId } }), [headers, sectionId]),
  })

  const workType = section.workType
  const isType2 = workType === '2'
  const isType3 = workType === '3'
  const competenciesData = sectionCompetencies.data.data

  return (
    <div className="w-full rounded-card bg-card px-6 py-9">
      <div className="mb-2 flex flex-nowrap items-center pl-0 font-bold text-primaryTextColor">
        {t('portfolio:competence')}
        <span className="pl-1 text-lightSecondaryWarning">*</span>
      </div>

      {competenciesData != null && isType2 && (
        <>
          {competenciesData.map((competencyItem, index) => (
            <div key={competencyItem.id} className="mb-6 rounded-sm border border-borderGrey p-4">
              <div className="mb-3 mr-4 flex items-center">
                <label className="text-primaryTextColor" htmlFor={competencyItem.id}>
                  <span className="font-bold">
                    {competencyItem.name}
                    <PopoverComponent>
                      <span
                        className="mb-3 flex py-2"
                        dangerouslySetInnerHTML={{ __html: competencyItem.description }}
                      />
                      {competencyItem.criterias?.map((criteria) => (
                        <div key={`${competencyItem.id}-${criteria.id}`} className="my-2">
                          <label className="mb-3 text-primaryTextColor">
                            <div className="my-3">
                              {criteria.name}
                              <div>
                                <span className="p-2" dangerouslySetInnerHTML={{ __html: criteria.description }} />
                              </div>
                            </div>
                          </label>
                        </div>
                      ))}
                    </PopoverComponent>
                  </span>
                </label>
              </div>
              <div className="flex">
                {competencyItem.criterias?.map((criteria, criteriaIndex) => (
                  <div key={`${competencyItem.id}-${criteria.id}`} className="mb-3 mr-2 flex w-full">
                    <TextareaInput
                      label={criteria.name}
                      name={`competencies[${index}]criterias[${criteriaIndex}]text`}
                      rows={5}
                      maxLength={section.wordLimits?.max}
                      minLength={section.wordLimits?.min}
                      required
                      countMode="words"
                      instructions={
                        criteria.description ? (
                          <PopoverComponent>
                            <span dangerouslySetInnerHTML={{ __html: criteria.description }} />
                          </PopoverComponent>
                        ) : undefined
                      }
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {competenciesData != null && !isType2 && (
        <div className="mb-6">
          {competenciesData.map((c) => (
            <span key={c.id} className="mb-3 mr-4 flex items-center">
              <input
                name="competencies"
                type="checkbox"
                id={c.id}
                className="border border-tableBorderColor"
                onChange={(e) => {
                  if (e.currentTarget.checked) {
                    formik.setFieldValue('competencies', [...formik.values.competencies, { competencyId: c.id }])
                  } else if (!isType3) {
                    formik.setFieldValue(
                      'competencies',
                      formik.values.competencies.filter((v) => v.competencyId.toString() !== c.id)
                    )
                  }
                }}
                checked={formik.values.competencies.some((v) => v.competencyId.toString() === c.id) || isType3}
              />
              <label className="text-primaryTextColor" htmlFor={c.id}>
                <span className="ml-2">
                  {c.name}
                  <PopoverComponent>
                    <span className="flex p-2" dangerouslySetInnerHTML={{ __html: c.description }} />
                    <div className="mb-6">
                      <div className="mb-2 flex flex-nowrap items-center pl-0 font-bold text-primaryTextColor">
                        {t('portfolio:criterias')}:
                      </div>
                      {competenciesData.map((criterias) =>
                        criterias.criterias?.map((criteria) => (
                          <div key={`${c.id}-${criteria.id}`} className="my-2">
                            <label className="mb-3 text-primaryTextColor">
                              <div className="my-3">
                                {criteria.name}
                                <div>
                                  <span className="p-2" dangerouslySetInnerHTML={{ __html: criteria.description }} />
                                </div>
                              </div>
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  </PopoverComponent>
                </span>
              </label>
            </span>
          ))}
        </div>
      )}

      <div className="mb-6 text-primaryTextColor">
        <SelectInput
          placeholder={t('common:choose')}
          label={t('portfolio:mentor')}
          name="mentorId"
          options={mentors.data.map((m) => ({ label: `${m.firstName} ${m.lastName}`, value: m.id })) ?? []}
        />
      </div>
      {!isType2 && (
        <div>
          <div className="mb-2 flex flex-nowrap items-center pl-0 font-bold text-primaryTextColor">
            {t('portfolio:add_assignment_description')}
          </div>
          <FormikTinyMce
            name="text"
            required
            countMode="words"
            minLength={section.wordLimits?.min}
            maxLength={section.wordLimits?.max}
          />
        </div>
      )}

      <div className="my-6" style={{ zIndex: 0, maxWidth: 1065 }}>
        <FileInput name="files" multiple config={Api.UploadConfigMap.studentPortfolio(sectionId)} />
      </div>

      <div className="mb-8">
        <h2 className="mb-3 border-b border-borderGrey py-2">{t('portfolio:assignment_status') + `: `}</h2>
        <div>
          {work.status === 'STATUS_DRAFT' && (
            <span className="text-primaryTextColor">{t('portfolio:status_draft')}</span>
          )}
          {work.status === 'STATUS_REJECTED' && (
            <span className="text-primaryTextColor">{t('portfolio:status_rejected')}</span>
          )}
        </div>
      </div>
      {work.comment != null && work.comment.length > 0 ? (
        <div className="mb-6">
          <h2 className="mb-3 border-b border-borderGrey py-2">{t('portfolio:lecturer_comment') + `: `}</h2>
          <div className="text-title text-primaryTextColor">{work.comment}</div>
        </div>
      ) : null}
      <div className="flex w-full justify-end">
        {work.manage?.canSaveAsDraft && work.status !== 'STATUS_REJECTED' && (
          <div className="mr-2 mt-10">
            <Button
              variant="blue"
              disabled={!formik.isValid || !formik.dirty}
              onClick={(e) => {
                e.preventDefault()
                formik.setFieldValue('isDraft', true)
                formik.submitForm()
              }}
            >
              {t('common:save_as_draft')}
            </Button>
          </div>
        )}
        {work.manage?.canEditWork ||
        work.status === 'STATUS_REJECTED' ||
        (work.status === 'STATUS_DRAFT' && work.manage?.canEditWork === false) ? (
          <div className="ml-2 mt-10">
            <Button
              type="button"
              variant="blue"
              onClick={(e) => {
                e.preventDefault()
                formik.setFieldValue('isDraft', false)
                formik.submitForm()
              }}
            >
              {t('common:submit')}
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
